import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, Square, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const TIPI = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];

export default function DictationButton({ onResult, contesto = '', className = '' }) {
  const [stato, setStato] = useState('idle'); // idle | recording | transcribing
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => () => {
    if (recorderRef.current && recorderRef.current.state === 'recording') recorderRef.current.stop();
    streamRef.current?.getTracks().forEach(t => t.stop());
  }, []);

  const trascrivi = async (blob) => {
    setStato('transcribing');
    const fd = new FormData();
    fd.append('file', blob, 'dettatura.webm');
    if (contesto) fd.append('contesto', contesto);
    try {
      const res = await api.post('/trascrizione', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const testo = (res.data.testo || '').trim();
      if (!testo) toast.warning('Nessun testo riconosciuto, riprova parlando piu vicino al microfono');
      else {
        onResult(prev => (prev ? `${prev} ${testo}` : testo));
        toast.success('Dettatura trascritta');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella trascrizione');
    }
    setStato('idle');
  };

  const avvia = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Il browser non supporta la registrazione audio');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = TIPI.find(t => MediaRecorder.isTypeSupported(t)) || '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        streamRef.current?.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        if (blob.size < 1200) { setStato('idle'); toast.warning('Registrazione troppo breve'); return; }
        trascrivi(blob);
      };
      recorder.start();
      recorderRef.current = recorder;
      setStato('recording');
      toast.info('Registrazione avviata - premi di nuovo per trascrivere');
    } catch {
      toast.error('Microfono non disponibile: verifica i permessi del browser');
    }
  };

  const ferma = () => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    recorderRef.current = null;
  };

  const toggle = () => {
    if (stato === 'recording') ferma();
    else if (stato === 'idle') avvia();
  };

  return (
    <Button
      type="button"
      variant={stato === 'recording' ? 'default' : 'ghost'}
      size="sm"
      onClick={toggle}
      disabled={stato === 'transcribing'}
      className={`h-7 w-7 p-0 flex-shrink-0 transition-all ${stato === 'recording' ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse shadow-lg shadow-red-200' : 'text-slate-400 hover:text-brand hover:bg-brand-soft'} ${className}`}
      title={stato === 'recording' ? 'Ferma e trascrivi' : stato === 'transcribing' ? 'Trascrizione in corso...' : 'Dettatura vocale'}
      data-testid="dictation-btn"
    >
      {stato === 'transcribing' ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
        : stato === 'recording' ? <Square className="w-3 h-3" />
          : <Mic className="w-3.5 h-3.5" />}
    </Button>
  );
}
